import { collection, query, where, getDocs, doc, writeBatch, limit, Query, DocumentData } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Updates all posts by a user when their profile information changes
 * @param userId The user ID whose posts need to be updated
 * @param updates Object containing the fields to update (userName and/or userPhotoURL)
 */
export const syncUserProfileWithPosts = async (
  userId: string,
  updates: {
    userName?: string;
    userPhotoURL?: string;
  }
) => {
  try {
    // Only proceed if there are actual updates
    if (!updates.userName && !updates.userPhotoURL) {
      return;
    }

    // Procesamos los posts en lotes para manejar grandes cantidades de datos
    const batchSize = 500; // Firestore permite hasta 500 operaciones por lote
    let lastDocId: string | null = null;
    let hasMoreDocs = true;

    while (hasMoreDocs) {
      // Query all posts by this user
      let postsQuery = query(
        collection(db, 'posts'),
        where('userId', '==', userId)
      );

      if (lastDocId) {
        // Si tenemos un lastDocId, comenzamos después de ese documento
        postsQuery = query(
          collection(db, 'posts'),
          where('userId', '==', userId),
          where('__name__', '>', lastDocId),
          limit(batchSize)
        );
      } else {
        // Primera consulta, limitamos a batchSize
        postsQuery = query(
          collection(db, 'posts'),
          where('userId', '==', userId),
          limit(batchSize)
        );
      }

      const querySnapshot = await getDocs(postsQuery);

      if (querySnapshot.empty) {
        hasMoreDocs = false;
        break;
      }

      // Crear un lote para actualizar múltiples documentos a la vez
      const batch = writeBatch(db);

      for (const postDoc of querySnapshot.docs) {
        const postRef = doc(db, 'posts', postDoc.id);
        const updateData: Record<string, string> = {};

        // Solo incluir campos que necesiten actualización
        if (updates.userName) {
          updateData.userName = updates.userName;
        }

        if (updates.userPhotoURL) {
          updateData.userPhotoURL = updates.userPhotoURL;
        }

        batch.update(postRef, updateData);

        // Guardar el ID del último documento procesado
        lastDocId = postDoc.id;
      }

      // Ejecutar el lote de actualizaciones
      await batch.commit();

      // Si recibimos menos documentos que el tamaño del lote, hemos terminado
      if (querySnapshot.size < batchSize) {
        hasMoreDocs = false;
      }
    }

    // eslint-disable-next-line no-console
    console.log(`Successfully updated posts for user ${userId}`);
  } catch (error) {
    console.error('Error synchronizing user profile with posts:', error);
    throw error;
  }
};

/**
 * Updates all comments by a user when their profile information changes
 * @param userId The user ID whose comments need to be updated
 * @param updates Object containing the fields to update (userName and/or userPhotoURL)
 */
export const syncUserProfileWithComments = async (
  userId: string,
  updates: {
    userName?: string;
    userPhotoURL?: string;
  }
) => {
  try {
    // Only proceed if there are actual updates
    if (!updates.userName && !updates.userPhotoURL) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log(`Iniciando sincronización de comentarios para el usuario ${userId}...`);

    // Query all posts (we'll need to check comments in each post)
    const postsQuery = query(collection(db, 'posts'));
    const querySnapshot = await getDocs(postsQuery);

    let totalCommentsUpdated = 0;

    // Procesar los comentarios en lotes para cada post
    for (const postDoc of querySnapshot.docs) {
      let lastCommentId: string | null = null;
      let hasMoreComments = true;

      while (hasMoreComments) {
        let commentsQuery: Query<DocumentData>;

        if (lastCommentId) {
          commentsQuery = query(
            collection(db, `posts/${postDoc.id}/comments`),
            where('userId', '==', userId),
            where('__name__', '>', lastCommentId),
            limit(500)
          );
        } else {
          commentsQuery = query(
            collection(db, `posts/${postDoc.id}/comments`),
            where('userId', '==', userId),
            limit(500)
          );
        }

        const commentsSnapshot = await getDocs(commentsQuery);

        if (commentsSnapshot.empty) {
          hasMoreComments = false;
          break;
        }

        const batch = writeBatch(db);
        let batchSize = 0;

        for (const commentDoc of commentsSnapshot.docs) {
          const commentRef = doc(db, `posts/${postDoc.id}/comments`, commentDoc.id);
          const updateData: Record<string, string> = {};

          if (updates.userName) {
            updateData.userName = updates.userName;
          }

          if (updates.userPhotoURL) {
            updateData.userPhotoURL = updates.userPhotoURL;
          }

          batch.update(commentRef, updateData);
          batchSize++;

          // Guardar el ID del último comentario procesado
          lastCommentId = commentDoc.id;
        }

        // Ejecutar el lote de actualizaciones
        if (batchSize > 0) {
          await batch.commit();
          totalCommentsUpdated += batchSize;
          // eslint-disable-next-line no-console
          console.log(`Actualizados ${batchSize} comentarios en el post ${postDoc.id}`);
        }

        // Si recibimos menos documentos que el tamaño del lote, hemos terminado con este post
        if (commentsSnapshot.size < 500) {
          hasMoreComments = false;
        }
      }
    }

    // eslint-disable-next-line no-console
    console.log(`Sincronización completada: Se actualizaron ${totalCommentsUpdated} comentarios para el usuario ${userId}`);
  } catch (error) {
    console.error('Error synchronizing user profile with comments:', error);
    throw error;
  }
};
